import { REGEX_EXTRACTION_ENABLED } from './RRDPdfConfig';
import { extractPdfPageTexts, PageText } from './RRDPdfTextExtractor';

// ── PDF field extraction (regex-based) ────────────────────────────────────────
export type ExtractedFields = {
  accountNumber?: string;
  mrn?: string;
  payerName?: string;
  address?: string;
  city?: string;
  state?: string;
  zip?: string;
  firstName?: string;
  lastName?: string;
  facilityName?: string;
};

export type ExtractedFieldScores = Partial<Record<keyof ExtractedFields, number>>;

export type ExtractedFieldsWithQuality = {
  fields: ExtractedFields;
  fieldScores: ExtractedFieldScores;
  overallScore: number;
};

export type FieldExtractionOptions = {
  excludedFields?: readonly (keyof ExtractedFields)[];
};

export const AUTOFILL_QUALITY_THRESHOLD = 0.5;

// All fields we attempt to extract. Used to decide when extraction is "complete".
const EXTRACTED_FIELD_KEYS: (keyof ExtractedFields)[] = [
  'accountNumber',
  'mrn',
  'payerName',
  'address',
  'city',
  'state',
  'zip',
  'firstName',
  'lastName',
  'facilityName',
];

const FIELD_QUALITY_WEIGHTS: Record<keyof ExtractedFields, number> = {
  accountNumber: 1.3,
  mrn: 1.2,
  payerName: 0.9,
  address: 1.0,
  city: 0.8,
  state: 0.7,
  zip: 0.8,
  firstName: 0.5,
  lastName: 0.5,
  facilityName: 0.9,
};

function clampQualityScore(score: number): number {
  return Number(Math.max(0, Math.min(1, score)).toFixed(2));
}

function computeOverallExtractionQuality(
  fields: ExtractedFields,
  fieldScores: ExtractedFieldScores,
): number {
  let weightedSum = 0;
  let totalWeight = 0;

  for (const key of EXTRACTED_FIELD_KEYS) {
    const value = fields[key];
    const score = fieldScores[key];
    if (!value || score === undefined) continue;

    const weight = FIELD_QUALITY_WEIGHTS[key];
    weightedSum += score * weight;
    totalWeight += weight;
  }

  return totalWeight > 0 ? clampQualityScore(weightedSum / totalWeight) : 0;
}

type ParsedFieldsWithQuality = {
  fields: ExtractedFields;
  fieldScores: ExtractedFieldScores;
};

type ScoredPattern = {
  pattern: RegExp;
  score: number;
};

// ── Shared regex patterns ──────────────────────────────────────────────────
// Hoisted to module scope so they are compiled once instead of being
// recreated on every page/field parse. Two address/PO-box variants are kept
// (general vs. payer-block) because they were deliberately tuned with
// different digit-length tolerances for their respective contexts.
const POBOX_PATTERN = /\b(P\.?\s*O\.?\s*B(?:OX|ox)\s+\d+(?:\s*-\s*\d+)?)\b/i;
const ADDRESS_PATTERN = /\b((?!(?:19|20)\d{2}\b)\d{1,4}\s+[A-Za-z0-9.#\-\s]+(?:Street|St\.?|Avenue|Ave\.?|Boulevard|Blvd\.?|Drive|Dr\.?|Road|Rd\.?|Lane|Ln\.?|Way|Court|Ct\.?|Circle|Cir\.?|Parkway|Pkwy\.?|Highway|Hwy\.?)\b(?:\s+(?:Suite|Ste\.?|Unit|Apt\.?|#)\s*[A-Za-z0-9-]+)?)\b/i;
const PAYER_BLOCK_POBOX_PATTERN = /\b(P\.?\s*O\.?\s*B(?:OX|ox)\s+\d+(?:\s*-\s*\d+)?)\b/i;
const PAYER_BLOCK_ADDRESS_PATTERN = /\b(\d{1,6}\s+[A-Za-z0-9.#\-\s]+(?:Street|St\.?|Avenue|Ave\.?|Boulevard|Blvd\.?|Drive|Dr\.?|Road|Rd\.?|Lane|Ln\.?|Way|Court|Ct\.?|Circle|Cir\.?|Parkway|Pkwy\.?|Highway|Hwy\.)\b(?:\s+(?:Suite|Ste\.?|Unit|Apt\.?|#)\s*[A-Za-z0-9-]+)?)\b/i;
const STREET_TYPE_PATTERN = /\b(?:AVE|AVENUE|BLVD|BOULEVARD|RD|ROAD|LN|LANE|WAY|CIR|CIRCLE|PKWY|PARKWAY)\b/i;
const STREET_TYPE_STRIP_PATTERN = /^.*\b(?:AVE|AVENUE|BLVD|BOULEVARD|RD|ROAD|LN|LANE|WAY|CIR|CIRCLE|PKWY|PARKWAY)\b\s*/i;
// State group intentionally accepts lower/mixed case (`[A-Za-z]{2}`) to still
// match OCR output that didn't preserve capitalization; the captured value is
// normalized to uppercase and validated wherever it is stored (see `setField`).
const CITY_STATE_ZIP_WITH_COMMA = /\b([A-Za-z][A-Za-z\s]{1,40}),\s*([A-Za-z]{2})\.?\s+(\d{5}(?:-\d{4})?)\b/;
const CITY_STATE_ZIP_NO_COMMA = /\b([A-Za-z][A-Za-z\s]{1,40})\s+([A-Za-z]{2})\.?\s+(\d{5}(?:-\d{4})?)\b/;

// Canonical USPS state/territory codes. Used to boost extraction confidence
// when the captured 2-letter code is a real state (vs. two incidental capital
// letters that happened to satisfy the shape check).
const VALID_STATE_CODES = new Set([
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA', 'HI', 'ID', 'IL', 'IN', 'IA',
  'KS', 'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT',
  'VA', 'WA', 'WV', 'WI', 'WY', 'DC', 'PR', 'VI', 'GU', 'AS', 'MP',
]);
const DATE_HEADER_PATTERN = /\bDate\s*:\s*(?:[A-Za-z]+\s+\d{1,2},?\s+\d{4}|\d{1,2}\/\d{1,2}\/\d{4})\b/i;
const NEXT_SECTION_PATTERN = /\b(?:Facility|Service\s+Date|Tax\s+ID|Account\s+[Nn]umber|Total\s+Charges)\s*[:/]/i;
const FACILITY_LABEL_PATTERN = /\bFacility\s*:/i;
const PHONE_PREFIX_PATTERN = /^(?:Phone|Ph|Tel|Fax)\s*[:#]?\s*[\d\s\-()+]+/i;
const LEADING_SPOKEN_DATE_PATTERN = /^(?:January|February|March|April|May|June|July|August|September|October|November|December)\s+\d{1,2},?\s+\d{4}\s*/i;

/** Finds a "City, ST 12345" (or "City ST 12345") match anywhere in `text`. */
function extractCityStateZip(text: string): RegExpMatchArray | null {
  return text.match(CITY_STATE_ZIP_WITH_COMMA) || text.match(CITY_STATE_ZIP_NO_COMMA);
}

/** Strips a leaked street-type word from a captured city (e.g. "FALLS AVE LAS VEGAS" → "LAS VEGAS"). */
function cleanCity(city: string): string {
  if (!STREET_TYPE_PATTERN.test(city)) return city;
  const trimmed = city.replace(STREET_TYPE_STRIP_PATTERN, '').trim();
  return trimmed || city;
}

/**
 * Parses the extracted fields from a single page's normalized text.
 */
function parseFieldsFromText(
  fullText: string,
  fullTextUpper: string,
  lines: string[],
  excludedFields: ReadonlySet<keyof ExtractedFields>,
): ParsedFieldsWithQuality {
  const fields: ExtractedFields = {};
  const fieldScores: ExtractedFieldScores = {};

  const setField = (field: keyof ExtractedFields, value: string | undefined, score: number) => {
    if (excludedFields.has(field)) return;

    let trimmedValue = value?.trim();
    if (!trimmedValue) return;

    // Guarantee state is always exactly 2 uppercase letters, regardless of
    // which code path produced the raw match (OCR text can be lower/mixed
    // case). Reject anything that doesn't reduce to that shape.
    if (field === 'state') {
      const normalizedState = trimmedValue.toUpperCase();
      if (!/^[A-Z]{2}$/.test(normalizedState)) return;
      trimmedValue = normalizedState;
    }

    const normalizedScore = clampQualityScore(score);
    if (!fields[field] || normalizedScore >= (fieldScores[field] ?? 0)) {
      fields[field] = trimmedValue as any;
      fieldScores[field] = normalizedScore;
    }
  };

  const extractFirst = (patterns: ScoredPattern[]): { value: string; score: number } | undefined => {
    for (const { pattern, score } of patterns) {
      const match = fullText.match(pattern);
      const value = match?.[1]?.trim();
      if (value) return { value, score };
    }
    return undefined;
  };

  const sanitizeName = (value: string | undefined): string | undefined => {
    if (!value) return undefined;
    const cleaned = value.replace(/\s+/g, ' ').trim().replace(/[.,;:]+$/, '');
    return cleaned || undefined;
  };

  const scoreIdentifier = (value: string, baseScore: number): number => {
    let score = baseScore;
    if (!/^[A-Z0-9-]{4,25}$/i.test(value)) score -= 0.25;
    if (value.length < 5) score -= 0.1;
    if (/^\d{4,25}$/.test(value)) score += 0.03;
    return clampQualityScore(score);
  };

  const scoreName = (value: string, baseScore: number): number => {
    let score = baseScore;
    const words = value.split(/\s+/).filter(Boolean);
    if (words.length < 2) score -= 0.15;
    if (value.length > 60) score -= 0.15;
    if (/^(?:to|attn|attention|re)$/i.test(value)) score -= 0.3;
    return clampQualityScore(score);
  };

  const scoreAddress = (
    address: string | undefined,
    city: string | undefined,
    state: string | undefined,
    zip: string | undefined,
    baseScore: number,
    streetPattern: RegExp,
    poBoxPattern: RegExp,
  ): number => {
    if (!address) return 0;

    let score = baseScore;
    if (streetPattern.test(address) || poBoxPattern.test(address)) score += 0.15;
    if (city) score += 0.05;
    if (state && /^[A-Z]{2}$/.test(state)) score += 0.05;
    if (zip && /^\d{5}(?:-\d{4})?$/.test(zip)) score += 0.05;
    return clampQualityScore(score);
  };

  const scoreCity = (value: string, baseScore: number): number => {
    let score = baseScore;
    if (!/^[A-Za-z][A-Za-z\s.'-]{1,40}$/.test(value)) score -= 0.2;
    return clampQualityScore(score);
  };

  const scoreState = (value: string, baseScore: number): number => {
    const normalized = value.toUpperCase();
    if (!/^[A-Z]{2}$/.test(normalized)) return clampQualityScore(baseScore - 0.25);
    return clampQualityScore(VALID_STATE_CODES.has(normalized) ? baseScore + 0.1 : baseScore - 0.15);
  };

  const scoreZip = (value: string, baseScore: number): number => (
    clampQualityScore(/^\d{5}(?:-\d{4})?$/.test(value) ? baseScore + 0.1 : baseScore - 0.25)
  );

  // Account Number
  const accountNumber = extractFirst([
    { pattern: /(?:Patient\s+)?Account\s*(?:Number|No\.?|#|Num)?\s*[:#-]?\s*([A-Z0-9-]{4,25})/i, score: 0.96 },
    { pattern: /(?:Acct|A\/C)\s*(?:No\.?|#)?\s*[:#-]?\s*([A-Z0-9-]{4,25})/i, score: 0.92 },
    { pattern: /Guarantor\s*(?:Account\s*)?(?:No\.?|#)?\s*[:#-]?\s*([A-Z0-9-]{4,25})/i, score: 0.86 },
    { pattern: /Insured\s+ID\s+(?:Number|No\.?|#)?\s*[:#-]?\s*([A-Z0-9-]{4,25})/i, score: 0.48 },
  ]);
  if (accountNumber) {
    setField('accountNumber', accountNumber.value, scoreIdentifier(accountNumber.value, accountNumber.score));
  }

  // MRN
  const mrn = extractFirst([
    { pattern: /(?:MRN|M\.R\.N\.?|Medical\s*Record\s*(?:Number|No\.?|#)?)\s*[:#-]?\s*([A-Z0-9-]{4,25})/i, score: 0.96 },
    { pattern: /(?:Member\s+ID|Subscriber\s+ID)\s*[:#-]?\s*([A-Z0-9-]{4,25})/i, score: 0.45 },
  ]);
  if (mrn) {
    setField('mrn', mrn.value, scoreIdentifier(mrn.value, mrn.score));
  }

  // Payer / Insurance
  const payerCandidate = extractFirst([
    { pattern: /(?:Insurance(?:\s+Company)?|Payer|Insurer|Plan\s+Name|Health\s+Plan)\s*[:#-]?\s*([A-Za-z][A-Za-z0-9\s&,./'-]{2,69}?)(?:\s{2,}|$)/i, score: 0.72 },
  ]);
  const sanitizedPayerCandidate = sanitizeName(payerCandidate?.value);
  if (sanitizedPayerCandidate && !['CARD', 'CARDHOLDER', 'ID CARD'].includes(sanitizedPayerCandidate.toUpperCase())) {
    setField('payerName', sanitizedPayerCandidate, scoreName(sanitizedPayerCandidate, payerCandidate?.score ?? 0.72));
  }

  // ── Split header: [FACILITY BLOCK] Date: [DATE] [PAYER BLOCK] ─────────────
  const dateHeaderMatch = fullText.match(DATE_HEADER_PATTERN);
  let facilityBlock = '';
  let payerBlock = '';
  if (dateHeaderMatch) {
    const dateEnd = dateHeaderMatch.index! + dateHeaderMatch[0].length;
    facilityBlock = fullText.slice(0, dateHeaderMatch.index!).trim();
    const afterDate = fullText.slice(dateEnd).trim();
    const nextSectionIdx = afterDate.search(NEXT_SECTION_PATTERN);
    payerBlock = nextSectionIdx > 0
      ? afterDate.slice(0, nextSectionIdx).trim()
      : nextSectionIdx === -1
        ? afterDate.slice(0, 300).trim()
        : '';
  }

  // ── Format 2: [FACILITY] [PAYER] Date: [DATE] — payer embedded before Date: ──────────
  if (!payerBlock && facilityBlock) {
    const facCszMatch = extractCityStateZip(facilityBlock);
    if (facCszMatch) {
      const afterFac = facilityBlock
        .slice(facCszMatch.index! + facCszMatch[0].length)
        .replace(/^[\s\d-]*/, '')
        .replace(PHONE_PREFIX_PATTERN, '')
        .trim();
      if (afterFac) payerBlock = afterFac;
    }
  }

  // ── Format 3: no Date: header — use Facility: label as header boundary ─────
  if (!payerBlock && !dateHeaderMatch) {
    const facilityLabelIdx = fullText.search(FACILITY_LABEL_PATTERN);
    if (facilityLabelIdx > 0) {
      const headerBlock = fullText.slice(0, facilityLabelIdx).trim();
      const headerCszMatch = extractCityStateZip(headerBlock);
      if (headerCszMatch) {
        if (!facilityBlock) {
          facilityBlock = headerBlock.slice(0, headerCszMatch.index!).trim();
        }
        const afterHeaderCity = headerBlock
          .slice(headerCszMatch.index! + headerCszMatch[0].length)
          .replace(/^[\s\d-]*/, '')
          .replace(PHONE_PREFIX_PATTERN, '')
          .trim();
        if (afterHeaderCity) payerBlock = afterHeaderCity;
      }
    }
  }

  // ── Extract payer name, address, city/state/zip from payer block ──────────
  if (payerBlock) {
    const payerCszMatch = extractCityStateZip(payerBlock);
    const payerPoboxPos    = payerBlock.search(PAYER_BLOCK_POBOX_PATTERN);
    const payerPoboxMatch  = payerBlock.match(PAYER_BLOCK_POBOX_PATTERN);
    const payerStreetPos   = payerBlock.search(PAYER_BLOCK_ADDRESS_PATTERN);
    const payerStreetMatch = payerBlock.match(PAYER_BLOCK_ADDRESS_PATTERN);
    if (payerCszMatch) {
      const city = cleanCity(payerCszMatch[1].trim());
      const state = payerCszMatch[2].toUpperCase();
      const zip = payerCszMatch[3];
      setField('city', city, scoreCity(city, 0.8));
      setField('state', state, scoreState(state, 0.8));
      setField('zip', zip, scoreZip(zip, 0.8));
    }
    if (payerPoboxPos >= 0 && payerPoboxMatch) {
      const address = payerPoboxMatch[1].replace(/\s+/g, ' ').trim();
      setField('address', address, scoreAddress(address, fields.city, fields.state, fields.zip, 0.82, ADDRESS_PATTERN, POBOX_PATTERN));
    } else if (payerStreetPos >= 0 && payerStreetMatch) {
      const address = payerStreetMatch[1].replace(/\s+/g, ' ').trim();
      setField('address', address, scoreAddress(address, fields.city, fields.state, fields.zip, 0.82, ADDRESS_PATTERN, POBOX_PATTERN));
    }
    const addrStart = payerPoboxPos >= 0 ? payerPoboxPos
                    : payerStreetPos >= 0 ? payerStreetPos
                    : payerCszMatch ? payerBlock.indexOf(payerCszMatch[1])
                    : -1;
    if (addrStart > 0) {
      const rawPayerName = payerBlock.slice(0, addrStart).replace(/\s+/g, ' ').trim();
      const normalizedPayerName = sanitizeName(rawPayerName);
      if (normalizedPayerName) {
        setField('payerName', normalizedPayerName, scoreName(normalizedPayerName, 0.68));
      }
    }
  }

  // Street address
  if (!fields.address) {
    const poboxFallbackMatch = fullText.match(POBOX_PATTERN);
    if (poboxFallbackMatch) {
      const address = poboxFallbackMatch[1].replace(/\s+/g, ' ').trim();
      setField('address', address, scoreAddress(address, fields.city, fields.state, fields.zip, 0.48, ADDRESS_PATTERN, POBOX_PATTERN));
    } else {
      const addressMatch = fullText.match(ADDRESS_PATTERN);
      if (addressMatch) {
        const address = addressMatch[1].replace(/\s+/g, ' ').trim();
        setField('address', address, scoreAddress(address, fields.city, fields.state, fields.zip, 0.44, ADDRESS_PATTERN, POBOX_PATTERN));
      }
    }
  }

  // City, State ZIP
  const cityStateZipMatch = extractCityStateZip(fullText);
  if (cityStateZipMatch && (!fields.city || !fields.state || !fields.zip)) {
    if (!fields.city) {
      const city = cleanCity(cityStateZipMatch[1].trim());
      setField('city', city, scoreCity(city, 0.46));
    }
    if (!fields.state) {
      const state = cityStateZipMatch[2].toUpperCase();
      setField('state', state, scoreState(state, 0.46));
    }
    if (!fields.zip) setField('zip', cityStateZipMatch[3], scoreZip(cityStateZipMatch[3], 0.46));
  }

  // Fallback: find address on the line before the city/state/zip line.
  if (!fields.address && fields.city && fields.state && fields.zip) {
    const locationLineMatcher = new RegExp(`${fields.city}\s*,?\s*${fields.state}\s+${fields.zip}`.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'i');
    const locationIndex = lines.findIndex((line) => locationLineMatcher.test(line));
    if (locationIndex > 0) {
      const previousLine = lines[locationIndex - 1];
      const fallbackAddressMatch = previousLine.match(ADDRESS_PATTERN);
      if (fallbackAddressMatch) {
        const address = fallbackAddressMatch[1].replace(/\s+/g, ' ').trim();
        setField('address', address, scoreAddress(address, fields.city, fields.state, fields.zip, 0.55, ADDRESS_PATTERN, POBOX_PATTERN));
      }
    }
  }

  // Patient name — "Last, First" format common in medical docs
  const patientLastFirst =
    fullText.match(/(?:Patient|Member)(?:\s+Name)?\s*[:#-]?\s*([A-Za-z'-]+),\s*([A-Za-z'-]+)/i) ??
    fullText.match(/\bName\s*[:#-]\s*([A-Za-z'-]+),\s*([A-Za-z'-]+)/i);
  if (patientLastFirst) {
    setField('lastName', patientLastFirst[1].trim(), 0.82);
    setField('firstName', patientLastFirst[2].trim(), 0.82);
  } else {
    const patientFirstLast =
      fullText.match(/(?:Patient|Member)(?:\s+Name)?\s*[:#-]?\s*([A-Za-z'-]+)\s+([A-Za-z'-]+)/i) ??
      fullText.match(/\bName\s*[:#-]\s*([A-Za-z'-]+)\s+([A-Za-z'-]+)/i);
    if (patientFirstLast) {
      setField('firstName', patientFirstLast[1].trim(), 0.72);
      setField('lastName', patientFirstLast[2].trim(), 0.72);
    }
  }

  // MRN fallback: number after patient name, before DOB or Claim #
  if (!fields.mrn) {
    const mrnAfterName = fullText.match(
      /Patient\s+Name\s*[:#-]?\s*(?:[A-Za-z'-]+\s+){1,4}(\d{4,12})(?=\s+(?:Patient\s+)?(?:DOB|Date\s+of\s+Birth)|\s+Claim\s*#)/i
    );
    if (mrnAfterName) setField('mrn', mrnAfterName[1], scoreIdentifier(mrnAfterName[1], 0.42));
  }

  // Facility name
  const facilityLabelMatch = fullText.match(
    /\b(?:Facility|Provider)\s*:\s*([A-Za-z][A-Za-z0-9\s&'-]{2,69}?)(?=\s+(?:Patient|Member|Account|Policy|Service|Claim|Total|MRN|Diagnosis|Date|Provider|Address)\b|\s+\d{5,}|\s{2,}|$)/i
  );
  if (facilityLabelMatch) {
    setField('facilityName', facilityLabelMatch[1].replace(/\s+/g, ' ').trim(), 0.8);
  } else if (facilityBlock) {
    const facPoboxPos  = facilityBlock.search(POBOX_PATTERN);
    const facStreetPos = facilityBlock.search(ADDRESS_PATTERN);
    const facAddrStart = facPoboxPos >= 0 ? facPoboxPos : facStreetPos >= 0 ? facStreetPos : -1;
    if (facAddrStart > 0) {
      const facilityName = facilityBlock
        .slice(0, facAddrStart)
        .replace(/\s+/g, ' ')
        .trim()
        // Strip leading spoken date (e.g. "April 16, 2026 Dignity Health" → "Dignity Health")
        .replace(LEADING_SPOKEN_DATE_PATTERN, '')
        .trim();
      setField('facilityName', facilityName, 0.62);
    }
  }

  // If the payer label is likely OCR junk, clear it so we do not auto-fill a bad value.
  if (fields.payerName && /^[A-Z]{1,6}$/.test(fields.payerName.toUpperCase()) && fields.payerName.length < 5) {
    delete fields.payerName;
    delete fieldScores.payerName;
  }

  // Clear payerName if it's unreasonably long (garbage capture).
  if (fields.payerName && fields.payerName.length > 80) {
    delete fields.payerName;
    delete fieldScores.payerName;
  }

  // Clear payerName if it's a routing/addressee line ("To: …").
  if (fields.payerName && /^To:\s/i.test(fields.payerName)) {
    delete fields.payerName;
    delete fieldScores.payerName;
  }

  // Clear payerName if it duplicates the facility name.
  if (fields.payerName && fields.facilityName &&
      fields.payerName.toUpperCase() === fields.facilityName.toUpperCase()) {
    delete fields.payerName;
    delete fieldScores.payerName;
  }

  // Clear payerName if it matches the patient's full name.
  if (fields.payerName && fields.firstName && fields.lastName) {
    const patientFull = `${fields.firstName} ${fields.lastName}`.toUpperCase();
    const patientFullRev = `${fields.lastName} ${fields.firstName}`.toUpperCase();
    const payer = fields.payerName.toUpperCase();
    if (payer === patientFull || payer === patientFullRev) {
      delete fields.payerName;
      delete fieldScores.payerName;
    }
  }

  // Keep state uppercased if we found a value from mixed-case OCR output.
  if (!fields.state) {
    const stateFallback = fullTextUpper.match(/\b([A-Z]{2})\s+\d{5}(?:-\d{4})?\b/);
    if (stateFallback) setField('state', stateFallback[1], scoreState(stateFallback[1], 0.42));
  }

  return { fields, fieldScores };
}

/**
 * Merges regex-parsed fields across already-extracted page texts, keeping the
 * highest-quality value found for each field. Stops once every field has been
 * populated. Returns an empty result immediately when regex extraction is
 * disabled via config, so the caller can fall through to Azure AI only.
 */
export function extractFieldsFromPageTexts(
  pageTexts: PageText[],
  options: FieldExtractionOptions = {},
): ExtractedFieldsWithQuality {
  const merged: ExtractedFields = {};
  const mergedScores: ExtractedFieldScores = {};
  const excludedFields = new Set(options.excludedFields || []);

  if (!REGEX_EXTRACTION_ENABLED) {
    return { fields: merged, fieldScores: mergedScores, overallScore: 0 };
  }

  const allFieldsFound = () =>
    EXTRACTED_FIELD_KEYS.filter((key) => !excludedFields.has(key)).every((key) => {
      const value = merged[key];
      return value !== undefined && value !== '';
    });

  for (const pageText of pageTexts) {
    const pageResult = parseFieldsFromText(
      pageText.fullText,
      pageText.fullTextUpper,
      pageText.lines,
      excludedFields,
    );

    // Merge: keep the highest-quality value found across pages.
    for (const key of EXTRACTED_FIELD_KEYS) {
      const candidate = pageResult.fields[key];
      const candidateScore = pageResult.fieldScores[key] ?? 0;
      const currentScore = mergedScores[key] ?? -1;
      if (candidate !== undefined && candidate !== '' && candidateScore >= currentScore) {
        merged[key] = candidate as any;
        mergedScores[key] = candidateScore;
      }
    }

    // Stop early once every field has been populated.
    if (allFieldsFound()) break;
  }

  return {
    fields: merged,
    fieldScores: mergedScores,
    overallScore: computeOverallExtractionQuality(merged, mergedScores),
  };
}

/**
 * Loops through pages of the PDF, extracting fields from each page and
 * merging newly-found values into the running result.
 *
 * Prefer `extractFieldsFromPageTexts` (via `analyzePdfDocument`) when
 * classification is also needed for the same file — this wrapper re-parses
 * the PDF from scratch and is kept for standalone/back-compat use.
 */
export async function extractFieldsFromPDFPageWithQuality(
  file: File,
  options: FieldExtractionOptions = {},
): Promise<ExtractedFieldsWithQuality> {
  const pageTexts = await extractPdfPageTexts(file);
  return extractFieldsFromPageTexts(pageTexts, options);
}
